import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { fileService } from '../../services/fileService';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import FileUpload from '../../components/FileUpload';
import LoadingSpinner from '../../components/LoadingSpinner';

const { FiSettings, FiSave, FiRefreshCw, FiImage, FiEye, FiEyeOff, FiLock } = FiIcons;

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    backgroundImage: localStorage.getItem('workspace_background') || '',
    showBackground: localStorage.getItem('workspace_background_enabled') !== 'false',
    portalTitle: localStorage.getItem('portal_title') || 'Information Portal',
    primaryColor: localStorage.getItem('primary_color') || '#3b82f6',
    loginLogo: localStorage.getItem('login_logo') || '',
    showLoginLogo: localStorage.getItem('login_logo_enabled') !== 'false'
  });
  const [uploading, setUploading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    // Apply changes immediately when settings change (for live preview)
    const timeoutId = setTimeout(() => {
      applyWorkspaceBackground();
      applyPrimaryColor();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [settings.backgroundImage, settings.showBackground, settings.primaryColor]);

  // Apply primary color to CSS custom properties
  const applyPrimaryColor = () => {
    const root = document.documentElement;
    const color = settings.primaryColor;
    
    // Convert hex to RGB for opacity variants
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
    };

    const rgb = hexToRgb(color);
    if (rgb) {
      // Set CSS custom properties for primary color variants
      root.style.setProperty('--color-primary-50', `${Math.min(255, rgb.r + 40)} ${Math.min(255, rgb.g + 40)} ${Math.min(255, rgb.b + 40)}`);
      root.style.setProperty('--color-primary-100', `${Math.min(255, rgb.r + 30)} ${Math.min(255, rgb.g + 30)} ${Math.min(255, rgb.b + 30)}`);
      root.style.setProperty('--color-primary-200', `${Math.min(255, rgb.r + 20)} ${Math.min(255, rgb.g + 20)} ${Math.min(255, rgb.b + 20)}`);
      root.style.setProperty('--color-primary-300', `${Math.min(255, rgb.r + 10)} ${Math.min(255, rgb.g + 10)} ${Math.min(255, rgb.b + 10)}`);
      root.style.setProperty('--color-primary-400', `${Math.min(255, rgb.r + 5)} ${Math.min(255, rgb.g + 5)} ${Math.min(255, rgb.b + 5)}`);
      root.style.setProperty('--color-primary-500', `${rgb.r} ${rgb.g} ${rgb.b}`);
      root.style.setProperty('--color-primary-600', `${Math.max(0, rgb.r - 10)} ${Math.max(0, rgb.g - 10)} ${Math.max(0, rgb.b - 10)}`);
      root.style.setProperty('--color-primary-700', `${Math.max(0, rgb.r - 20)} ${Math.max(0, rgb.g - 20)} ${Math.max(0, rgb.b - 20)}`);
      root.style.setProperty('--color-primary-800', `${Math.max(0, rgb.r - 30)} ${Math.max(0, rgb.g - 30)} ${Math.max(0, rgb.b - 30)}`);
      root.style.setProperty('--color-primary-900', `${Math.max(0, rgb.r - 40)} ${Math.max(0, rgb.g - 40)} ${Math.max(0, rgb.b - 40)}`);
    }
  };

  const applyWorkspaceBackground = () => {
    const body = document.body;
    
    // Clear existing background and overlay
    body.style.backgroundImage = '';
    body.style.backgroundSize = '';
    body.style.backgroundPosition = '';
    body.style.backgroundRepeat = '';
    body.style.backgroundAttachment = '';
    
    // Remove existing overlay
    const existingOverlay = document.getElementById('workspace-overlay');
    if (existingOverlay) {
      existingOverlay.remove();
    }

    if (settings.showBackground && settings.backgroundImage && settings.backgroundImage.trim() !== '') {
      console.log('Applying background in settings:', settings.backgroundImage);
      
      body.style.backgroundImage = `url("${settings.backgroundImage}")`;
      body.style.backgroundSize = 'cover';
      body.style.backgroundPosition = 'center';
      body.style.backgroundRepeat = 'no-repeat';
      body.style.backgroundAttachment = 'fixed';

      // Add overlay for 75% opacity effect
      const overlay = document.createElement('div');
      overlay.id = 'workspace-overlay';
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(255, 255, 255, 0.75);
        pointer-events: none;
        z-index: -1;
      `;
      document.body.appendChild(overlay);
    }
  };

  const handleBackgroundUpload = async (file, preview) => {
    if (!file) {
      setSettings(prev => ({ ...prev, backgroundImage: '' }));
      return;
    }

    setUploading(true);
    try {
      // Validate and upload the background image
      fileService.validateImageFile(file, 10 * 1024 * 1024); // 10MB limit for backgrounds
      const uploadResult = await fileService.uploadFile(file);
      setSettings(prev => ({
        ...prev,
        backgroundImage: uploadResult.url,
        showBackground: true
      }));
    } catch (error) {
      alert(`Upload error: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleLogoUpload = async (file, preview) => {
    if (!file) {
      setSettings(prev => ({ ...prev, loginLogo: '' }));
      return;
    }

    setUploading(true);
    try {
      // Validate and upload the login logo
      fileService.validateImageFile(file, 5 * 1024 * 1024); // 5MB limit for logos
      const uploadResult = await fileService.uploadFile(file);
      setSettings(prev => ({
        ...prev,
        loginLogo: uploadResult.url,
        showLoginLogo: true
      }));
    } catch (error) {
      alert(`Upload error: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleSaveSettings = () => {
    console.log('Saving settings:', settings);
    
    // Save to localStorage
    localStorage.setItem('workspace_background', settings.backgroundImage);
    localStorage.setItem('workspace_background_enabled', settings.showBackground.toString());
    localStorage.setItem('portal_title', settings.portalTitle);
    localStorage.setItem('primary_color', settings.primaryColor);
    localStorage.setItem('login_logo', settings.loginLogo);
    localStorage.setItem('login_logo_enabled', settings.showLoginLogo.toString());

    // Update document title
    document.title = settings.portalTitle;
    
    // Apply colors and background immediately after saving
    applyPrimaryColor();
    applyWorkspaceBackground();
    
    // Trigger custom events to notify other components
    window.dispatchEvent(new CustomEvent('themeChanged', { 
      detail: { 
        primaryColor: settings.primaryColor,
        portalTitle: settings.portalTitle,
        background: settings.backgroundImage
      } 
    }));

    window.dispatchEvent(new CustomEvent('backgroundChanged'));
    
    alert('Settings saved successfully! The new theme has been applied.');
  };

  const handleResetBackground = () => {
    if (window.confirm('Are you sure you want to remove the background image?')) {
      setSettings(prev => ({
        ...prev,
        backgroundImage: '',
        showBackground: false
      }));
    }
  };

  const handleResetLogo = () => {
    if (window.confirm('Are you sure you want to remove the login logo?')) {
      setSettings(prev => ({
        ...prev,
        loginLogo: '',
        showLoginLogo: false
      }));
    }
  };

  const togglePreviewMode = () => {
    setPreviewMode(!previewMode);
  };

  const presetColors = [
    { name: 'Blue', value: '#3b82f6' },
    { name: 'Purple', value: '#8b5cf6' },
    { name: 'Green', value: '#10b981' },
    { name: 'Orange', value: '#f59e0b' },
    { name: 'Red', value: '#ef4444' },
    { name: 'Pink', value: '#ec4899' },
    { name: 'Indigo', value: '#6366f1' },
    { name: 'Teal', value: '#14b8a6' }
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
              <SafeIcon icon={FiSettings} className="h-8 w-8 text-primary-600" />
              <span>Workspace Settings</span>
            </h1>
            <p className="mt-2 text-gray-600">
              Customize your portal's appearance and branding.
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={togglePreviewMode}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <SafeIcon icon={previewMode ? FiEyeOff : FiEye} className="h-4 w-4" />
              <span>{previewMode ? 'Exit Preview' : 'Preview'}</span>
            </button>
            <button
              onClick={handleSaveSettings}
              disabled={uploading}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              <SafeIcon icon={FiSave} className="h-4 w-4" />
              <span>Save Settings</span>
            </button>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        {/* Login Logo Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
              <SafeIcon icon={FiLock} className="h-5 w-5 text-primary-600" />
              <span>Login Page Logo</span>
            </h3>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.showLoginLogo}
                onChange={(e) => setSettings(prev => ({ ...prev, showLoginLogo: e.target.checked }))}
                className="text-primary-600 focus:ring-primary-500 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Show Custom Logo</span>
            </label>
          </div>

          <div className="space-y-4">
            <FileUpload
              onFileSelect={handleLogoUpload}
              currentImage={settings.loginLogo}
              accept="image/png,image/jpeg,image/jpg,image/svg+xml"
              maxSize={5 * 1024 * 1024} // 5MB limit
            />

            {settings.loginLogo && (
              <div className="space-y-3">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Preview</h4>
                  <div className="flex items-center justify-center bg-white rounded-lg border p-4">
                    <div className="w-24 h-24 flex items-center justify-center">
                      <img
                        src={settings.loginLogo}
                        alt="Login logo preview"
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <div className="text-sm text-red-500" style={{ display: 'none' }}>
                        Failed to load logo
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleResetLogo}
                  className="w-full px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors text-sm"
                >
                  Remove Login Logo
                </button>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Logo Guidelines</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Square or horizontal logos work best (1:1 or 16:9 ratio)</li>
                <li>• Recommended size: 200x200px or larger</li>
                <li>• Supports PNG, JPG, and SVG formats (max 5MB)</li>
                <li>• Logo will be displayed at 96x96px on the login page</li>
                <li>• Use transparent backgrounds for best results</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Background Image Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
              <SafeIcon icon={FiImage} className="h-5 w-5 text-primary-600" />
              <span>Background Image</span>
            </h3>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.showBackground}
                onChange={(e) => setSettings(prev => ({ ...prev, showBackground: e.target.checked }))}
                className="text-primary-600 focus:ring-primary-500 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Enable Background</span>
            </label>
          </div>

          <div className="space-y-4">
            <FileUpload
              onFileSelect={handleBackgroundUpload}
              currentImage={settings.backgroundImage}
              accept="image/png,image/jpeg,image/jpg,image/webp"
              maxSize={10 * 1024 * 1024} // 10MB limit
            />

            {settings.backgroundImage && (
              <div className="space-y-3">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Preview</h4>
                  <div
                    className="w-full h-32 rounded border bg-cover bg-center relative overflow-hidden"
                    style={{ backgroundImage: `url("${settings.backgroundImage}")` }}
                  >
                    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
                      <span className="text-sm text-gray-600 font-medium">75% Opacity Overlay</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleResetBackground}
                  className="w-full px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors text-sm"
                >
                  Remove Background Image
                </button>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Background Tips</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Use high-resolution images (1920x1080 or larger)</li>
                <li>• Choose images with subtle patterns or low contrast</li>
                <li>• The background will have 75% white overlay for readability</li>
                <li>• Supported formats: PNG, JPG, WEBP (max 10MB)</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* General Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center space-x-2">
            <SafeIcon icon={FiSettings} className="h-5 w-5 text-primary-600" />
            <span>General Settings</span>
          </h3>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Portal Title
              </label>
              <input
                type="text"
                value={settings.portalTitle}
                onChange={(e) => setSettings(prev => ({ ...prev, portalTitle: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Information Portal"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Primary Color
              </label>
              <div className="space-y-4">
                {/* Color Picker */}
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    value={settings.primaryColor}
                    onChange={(e) => setSettings(prev => ({ ...prev, primaryColor: e.target.value }))}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={settings.primaryColor}
                    onChange={(e) => setSettings(prev => ({ ...prev, primaryColor: e.target.value }))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                    placeholder="#3b82f6"
                  />
                </div>
                
                {/* Preset Colors */}
                <div>
                  <div className="text-sm text-gray-600 mb-2">Quick Presets:</div>
                  <div className="grid grid-cols-4 gap-2">
                    {presetColors.map((preset) => (
                      <button
                        key={preset.name}
                        onClick={() => setSettings(prev => ({ ...prev, primaryColor: preset.value }))}
                        className={`flex items-center space-x-2 p-2 rounded-lg border text-sm transition-colors ${
                          settings.primaryColor === preset.value 
                            ? 'border-gray-400 bg-gray-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        title={preset.name}
                      >
                        <div 
                          className="w-4 h-4 rounded-full border border-gray-300"
                          style={{ backgroundColor: preset.value }}
                        />
                        <span className="text-xs text-gray-700 truncate">{preset.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Live Preview */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Color Preview</h4>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <div 
                        className="px-3 py-2 rounded text-white text-sm font-medium"
                        style={{ backgroundColor: settings.primaryColor }}
                      >
                        Primary Button
                      </div>
                      <div 
                        className="px-3 py-2 rounded border text-sm"
                        style={{ 
                          borderColor: settings.primaryColor,
                          color: settings.primaryColor 
                        }}
                      >
                        Outline Button
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <SafeIcon 
                        icon={FiSettings} 
                        className="h-5 w-5" 
                        style={{ color: settings.primaryColor }}
                      />
                      <span className="text-sm text-gray-700">Icon Color</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Current Settings Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-medium text-gray-900 mb-6">Current Configuration</h3>
          
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Active Settings</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Login Logo:</span>
                  <span className={`font-medium ${settings.showLoginLogo && settings.loginLogo ? 'text-green-600' : 'text-gray-500'}`}>
                    {settings.showLoginLogo && settings.loginLogo ? 'Custom Logo' : 'Default Icon'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Background:</span>
                  <span className={`font-medium ${settings.showBackground && settings.backgroundImage ? 'text-green-600' : 'text-gray-500'}`}>
                    {settings.showBackground && settings.backgroundImage ? 'Custom Image' : 'Default'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Portal Title:</span>
                  <span className="font-medium text-gray-900">{settings.portalTitle}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Primary Color:</span>
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-4 h-4 rounded border border-gray-300"
                      style={{ backgroundColor: settings.primaryColor }}
                    />
                    <span className="font-medium text-gray-900 font-mono text-xs">{settings.primaryColor}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Debug Info */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-yellow-900 mb-2">Debug Info</h4>
              <div className="space-y-1 text-xs text-yellow-800">
                <div>Background URL: {settings.backgroundImage || 'None'}</div>
                <div>Background Enabled: {settings.showBackground ? 'Yes' : 'No'}</div>
                <div>Stored BG: {localStorage.getItem('workspace_background') || 'None'}</div>
                <div>Stored BG Enabled: {localStorage.getItem('workspace_background_enabled')}</div>
              </div>
            </div>

            {/* Preview Login Page */}
            {settings.showLoginLogo && settings.loginLogo && (
              <div className="bg-gradient-to-br from-primary-50 to-secondary-100 rounded-lg p-6">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Login Page Preview</h4>
                <div className="flex flex-col items-center space-y-4 bg-white rounded-lg p-6 border">
                  <div className="w-24 h-24 flex items-center justify-center">
                    <img
                      src={settings.loginLogo}
                      alt="Login logo"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <div className="text-center">
                    <h2 className="text-xl font-bold text-gray-900">{settings.portalTitle}</h2>
                    <p className="text-sm text-gray-600 mt-1">Sign in to access your dashboards</p>
                  </div>
                  <div 
                    className="px-4 py-2 rounded text-white text-sm font-medium"
                    style={{ backgroundColor: settings.primaryColor }}
                  >
                    Sign In Button Preview
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Preview Mode Indicator */}
      {previewMode && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="fixed top-20 right-4 z-50 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg"
        >
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiEye} className="h-4 w-4" />
            <span className="text-sm font-medium">Preview Mode Active</span>
          </div>
        </motion.div>
      )}

      {/* Loading Overlay */}
      {uploading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50"
        >
          <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
            <LoadingSpinner size="md" />
            <span className="text-gray-900">Uploading image...</span>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default AdminSettings;